import PageHeader from "../components/PageHeader";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { ArrowUpRight } from "lucide-react";
import { motion } from "framer-motion";

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/blog")
      .then((r) => setPosts(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const [featured, ...rest] = posts;

  return (
    <div data-testid="blog-page">
      <PageHeader
        eyebrow="Insights"
        title="Field notes from the trading floor."
        subtitle="Short, honest analysis of the markets we trade — and the ones we watch."
      />

      <section className="py-24 sm:py-32 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          {loading ? (
            <div className="text-center text-[#4B5668]">Loading…</div>
          ) : (
            <>
              {featured && (
                <Link
                  to={`/blog/${featured.slug}`}
                  data-testid={`blog-featured-${featured.id}`}
                  className="group block mb-20"
                >
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-7 aspect-[16/10] overflow-hidden bg-[#F4F5F8]">
                      <img
                        src={featured.image}
                        alt={featured.title}
                        className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                      />
                    </div>
                    <div className="lg:col-span-5">
                      <div className="text-eyebrow text-[#F37021]">Featured · {featured.category}</div>
                      <h2 className="mt-5 font-display font-black tracking-tighter text-4xl sm:text-5xl leading-[1.02] group-hover:text-[#F37021] transition-colors">
                        {featured.title}
                      </h2>
                      <p className="mt-6 text-base text-[#4B5668] leading-relaxed">
                        {featured.excerpt}
                      </p>
                      <div className="mt-8 flex items-center gap-4 text-xs uppercase tracking-[0.18em] text-[#4B5668]">
                        <span>{featured.author}</span>
                        <span className="w-1 h-1 bg-[#4B5668] rounded-full" />
                        <span>{featured.published_at}</span>
                        <span className="w-1 h-1 bg-[#4B5668] rounded-full" />
                        <span>{featured.read_minutes} min</span>
                      </div>
                      <div className="mt-8 inline-flex items-center gap-3 text-sm font-semibold uppercase tracking-[0.18em]">
                        Read article
                        <ArrowUpRight className="w-4 h-4 arrow-icon" />
                      </div>
                    </div>
                  </div>
                </Link>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
                {rest.map((p, i) => (
                  <motion.article
                    key={p.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
                    data-testid={`blog-card-${p.id}`}
                  >
                    <Link to={`/blog/${p.slug}`} className="group block">
                      <div className="aspect-[4/3] overflow-hidden bg-[#F4F5F8]">
                        <img
                          src={p.image}
                          alt={p.title}
                          className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                        />
                      </div>
                      <div className="mt-6">
                        <div className="text-eyebrow text-[#F37021]">{p.category}</div>
                        <h3 className="mt-3 font-display font-black tracking-tighter text-2xl leading-[1.12] group-hover:text-[#F37021] transition-colors">
                          {p.title}
                        </h3>
                        <p className="mt-3 text-sm text-[#4B5668] leading-relaxed">
                          {p.excerpt}
                        </p>
                        <div className="mt-5 flex items-center gap-3 text-[11px] uppercase tracking-[0.18em] text-[#4B5668]">
                          <span>{p.author}</span>
                          <span className="w-1 h-1 bg-[#4B5668] rounded-full" />
                          <span>{p.published_at}</span>
                        </div>
                      </div>
                    </Link>
                  </motion.article>
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
